import { FC } from 'react';

import { MoreHorizontal, Package, Store, Trash2, Truck } from 'lucide-react';

import {
    extractItemName,
    hasRequiredSavedFields,
    savedProfileForMethod,
} from './checkoutHelpers';
import {
    DeliveryMethod,
    SavedProfile,
    SellerDeliveryForm,
    SellerGroup,
} from './checkoutTypes';
import { NovaPoshtaCitySelect } from '../../components/nova_poshta/NovaPoshtaCitySelect';
import { NovaPoshtaWarehouseSelect } from '../../components/nova_poshta/NovaPoshtaWarehouseSelect';
import { OfferPropertiesText } from '../../components/shared/OfferPropertiesText';
import { ScalePreview } from '../../components/shared/ScalePreview.tsx';
import { Card, CardContent } from '../../components/ui/Card';
import { Image } from '../../components/ui/Image';
import { Input } from '../../components/ui/Input';
import { UkrposhtaOfficeSelect } from '../../components/ukrposhta/UkrposhtaOfficeSelect';
import { UkrposhtaSettlementSelect } from '../../components/ukrposhta/UkrposhtaSettlementSelect';
import { CartItem } from '../../context/cart-context';
import { OfferProperty } from '../../types';

const METHOD_META: Record<
    DeliveryMethod,
    { label: string; description: string; icon: typeof Truck }
> = {
    nova_poshta: {
        label: 'Нова Пошта (Відділення)',
        description: 'Відділення або поштомат',
        icon: Truck,
    },
    ukrposhta: {
        label: 'Укрпошта',
        description: 'Доставка у поштове відділення',
        icon: Package,
    },
    pickup: {
        label: 'Самовивіз',
        description: 'Забрати особисто у продавця',
        icon: Store,
    },
};

interface Props {
    group: SellerGroup;
    form: SellerDeliveryForm;
    savedDeliveryProfiles: {
        nova_poshta: SavedProfile | null;
        ukrposhta: SavedProfile | null;
    };
    expandedDeliveryEditor: boolean;
    formErrors: Record<string, string>;
    itemActionsOpen: Record<string, boolean>;
    onToggleItemActions: (itemId: CartItem['id']) => void;
    onRemoveItem: (itemId: CartItem['id']) => void;
    onSetMethod: (sellerKey: string, method: DeliveryMethod) => void;
    onExpandDeliveryEditor: (sellerKey: string) => void;
    onUpdateField: (
        sellerKey: string,
        field: keyof SellerDeliveryForm,
        value: string,
    ) => void;
    onPatchForm: (
        sellerKey: string,
        patch: Partial<SellerDeliveryForm>,
    ) => void;
    offerProperties: OfferProperty[];
}

export const SellerCheckoutCard: FC<Props> = ({
    group,
    form,
    savedDeliveryProfiles,
    expandedDeliveryEditor,
    formErrors,
    itemActionsOpen,
    onToggleItemActions,
    onRemoveItem,
    onSetMethod,
    onExpandDeliveryEditor,
    onUpdateField,
    onPatchForm,
    offerProperties,
}) => {
    const sellerSubtotal = group.items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
    );
    const showSavedSummary =
        form.method !== '' &&
        form.method !== 'pickup' &&
        !expandedDeliveryEditor &&
        hasRequiredSavedFields(
            form.method,
            savedProfileForMethod(form.method, savedDeliveryProfiles),
        );
    const getError = (field: keyof SellerDeliveryForm) =>
        formErrors[`${group.key}.${field}`];

    return (
        <Card key={group.key}>
            <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <h2 className="text-lg font-semibold">
                            Продавець: {group.sellerName}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Позицій від цього продавця: {group.items.length}
                        </p>
                    </div>
                    <div className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-muted/50 dark:text-muted-foreground">
                        Доставка для цього продавця
                    </div>
                </div>

                {group.allowedMethods.length > 0 ? (
                    <div className="space-y-4">
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-muted-foreground">
                                Товари продавця
                            </p>
                            <div className="space-y-2">
                                {group.items.map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 dark:bg-muted/50"
                                    >
                                        <ScalePreview triggerClassName="relative h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-white dark:bg-background">
                                            <Image
                                                src={item.image}
                                                alt={item.imageAlt || item.name}
                                                className="object-cover"
                                            />
                                        </ScalePreview>
                                        <div className="min-w-0 flex-1">
                                            <p className="line-clamp-2 text-sm font-medium">
                                                {extractItemName(item.name)}
                                            </p>
                                            <OfferPropertiesText
                                                propertySet={item.propertySet}
                                                legacyCondition={item.condition}
                                                offerProperties={
                                                    offerProperties
                                                }
                                                productTypeId={
                                                    item.productTypeId
                                                }
                                                className="mt-1"
                                                dataTestId={`checkout-offer-properties-${item.id}`}
                                            />
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                Кількість: {item.quantity} ·
                                                Ціна: ₴{item.price.toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <p className="text-sm font-semibold whitespace-nowrap">
                                                ₴
                                                {(
                                                    item.price * item.quantity
                                                ).toFixed(2)}
                                            </p>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onToggleItemActions(
                                                            item.id,
                                                        )
                                                    }
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-gray-200 bg-white text-muted-foreground transition-colors hover:bg-gray-50 hover:text-foreground dark:border-border dark:bg-background dark:hover:bg-accent"
                                                    aria-label={`Дії для ${extractItemName(item.name)}`}
                                                >
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </button>
                                                {itemActionsOpen[
                                                    String(item.id)
                                                ] && (
                                                    <div className="absolute top-9 right-0 z-20 min-w-[160px] rounded-md border bg-white p-1 shadow-lg dark:border-border dark:bg-background">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                onRemoveItem(
                                                                    item.id,
                                                                )
                                                            }
                                                            className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                            Видалити товар
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between border-t pt-2 text-sm">
                                <span className="text-muted-foreground">
                                    Разом по продавцю
                                </span>
                                <span className="font-semibold">
                                    ₴{sellerSubtotal.toFixed(2)}
                                </span>
                            </div>
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium">
                                Спосіб доставки *
                            </label>
                            {!form.method && (
                                <p className="mb-2 text-xs text-muted-foreground">
                                    Оберіть один із доступних способів доставки
                                </p>
                            )}
                            <div className="space-y-3">
                                {group.allowedMethods.map((method) => (
                                    <label
                                        key={method}
                                        className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
                                            form.method === method
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-primary/30'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name={`delivery_method_${group.key}`}
                                            value={method}
                                            checked={form.method === method}
                                            onChange={() =>
                                                onSetMethod(group.key, method)
                                            }
                                            className="sr-only"
                                        />
                                        <div
                                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                                form.method === method
                                                    ? 'bg-primary text-white'
                                                    : 'bg-muted text-muted-foreground'
                                            }`}
                                        >
                                            {(() => {
                                                const Icon =
                                                    METHOD_META[method].icon;
                                                return (
                                                    <Icon className="h-4 w-4" />
                                                );
                                            })()}
                                        </div>
                                        <div>
                                            <div className="font-medium">
                                                {METHOD_META[method].label}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {
                                                    METHOD_META[method]
                                                        .description
                                                }
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            {formErrors[`${group.key}.method`] && (
                                <p className="mt-1 text-xs text-red-500">
                                    {formErrors[`${group.key}.method`]}
                                </p>
                            )}
                        </div>

                        {showSavedSummary && (
                            <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="space-y-1">
                                        <p className="text-sm font-semibold">
                                            Використовуємо збережені дані
                                            доставки
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {form.recipient_name} ·{' '}
                                            {form.recipient_phone}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {form.city}
                                            {form.region
                                                ? `, ${form.region}`
                                                : ''}
                                        </p>
                                        {form.method === 'nova_poshta' && (
                                            <p className="text-sm text-muted-foreground">
                                                {form.delivery_point_type ===
                                                'parcel_locker'
                                                    ? 'Поштомат'
                                                    : 'Відділення'}{' '}
                                                №{form.delivery_point_number}
                                            </p>
                                        )}
                                        {form.method === 'ukrposhta' && (
                                            <p className="text-sm text-muted-foreground">
                                                {form.post_office}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onExpandDeliveryEditor(group.key)
                                        }
                                        className="rounded-xl border border-primary/40 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 dark:text-primary-foreground dark:hover:bg-primary/15"
                                    >
                                        Змінити
                                    </button>
                                </div>
                            </div>
                        )}

                        {form.method !== '' &&
                            form.method !== 'pickup' &&
                            !showSavedSummary && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-medium">
                                            ПІБ отримувача *
                                        </label>
                                        <Input
                                            value={form.recipient_name}
                                            onChange={(e) =>
                                                onUpdateField(
                                                    group.key,
                                                    'recipient_name',
                                                    e.target.value,
                                                )
                                            }
                                            className={
                                                getError('recipient_name')
                                                    ? 'border-red-500'
                                                    : ''
                                            }
                                        />
                                        {getError('recipient_name') && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {getError('recipient_name')}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium">
                                            Телефон *
                                        </label>
                                        <Input
                                            value={form.recipient_phone}
                                            onChange={(e) =>
                                                onUpdateField(
                                                    group.key,
                                                    'recipient_phone',
                                                    e.target.value,
                                                )
                                            }
                                            className={
                                                getError('recipient_phone')
                                                    ? 'border-red-500'
                                                    : ''
                                            }
                                        />
                                        {getError('recipient_phone') && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {getError('recipient_phone')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                        {form.method === 'nova_poshta' && !showSavedSummary && (
                            <div className="space-y-4">
                                <NovaPoshtaCitySelect
                                    value={
                                        form.nova_poshta_city_name || form.city
                                    }
                                    onSelect={(city) => {
                                        onPatchForm(group.key, {
                                            city: city.name,
                                            nova_poshta_city_ref: city.ref,
                                            nova_poshta_city_name: city.name,
                                            delivery_point_number: '',
                                            nova_poshta_warehouse_ref: '',
                                            nova_poshta_warehouse_number: '',
                                            nova_poshta_warehouse_address: '',
                                        });
                                    }}
                                    onSelectionClear={() => {
                                        onPatchForm(group.key, {
                                            city: '',
                                            nova_poshta_city_ref: '',
                                            nova_poshta_city_name: '',
                                            delivery_point_number: '',
                                            nova_poshta_warehouse_ref: '',
                                            nova_poshta_warehouse_number: '',
                                            nova_poshta_warehouse_address: '',
                                        });
                                    }}
                                    onFallbackInput={(val) => {
                                        onUpdateField(group.key, 'city', val);
                                        onUpdateField(
                                            group.key,
                                            'nova_poshta_city_name',
                                            val,
                                        );
                                    }}
                                    error={getError('city')}
                                    allowManualEntry={false}
                                />

                                <div className="space-y-1">
                                    <label className="text-sm font-medium">
                                        Тип точки доставки
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                checked={
                                                    form.delivery_point_type ===
                                                    'branch'
                                                }
                                                onChange={() =>
                                                    onPatchForm(group.key, {
                                                        delivery_point_type:
                                                            'branch',
                                                        delivery_point_number:
                                                            '',
                                                        nova_poshta_warehouse_ref:
                                                            '',
                                                        nova_poshta_warehouse_number:
                                                            '',
                                                        nova_poshta_warehouse_address:
                                                            '',
                                                    })
                                                }
                                                className="accent-primary"
                                            />
                                            Відділення
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                checked={
                                                    form.delivery_point_type ===
                                                    'parcel_locker'
                                                }
                                                onChange={() =>
                                                    onPatchForm(group.key, {
                                                        delivery_point_type:
                                                            'parcel_locker',
                                                        delivery_point_number:
                                                            '',
                                                        nova_poshta_warehouse_ref:
                                                            '',
                                                        nova_poshta_warehouse_number:
                                                            '',
                                                        nova_poshta_warehouse_address:
                                                            '',
                                                    })
                                                }
                                                className="accent-primary"
                                            />
                                            Поштомат
                                        </label>
                                    </div>
                                </div>

                                <NovaPoshtaWarehouseSelect
                                    cityRef={form.nova_poshta_city_ref}
                                    type={form.delivery_point_type}
                                    value={
                                        form.nova_poshta_warehouse_number
                                            ? `${form.delivery_point_type === 'parcel_locker' ? 'Поштомат' : 'Відділення'} №${form.nova_poshta_warehouse_number}`
                                            : form.delivery_point_number
                                    }
                                    onSelect={(wh) => {
                                        onPatchForm(group.key, {
                                            delivery_point_number: wh.number,
                                            nova_poshta_warehouse_ref: wh.ref,
                                            nova_poshta_warehouse_number:
                                                wh.number,
                                            nova_poshta_warehouse_address:
                                                wh.address,
                                        });
                                    }}
                                    onSelectionClear={() => {
                                        onPatchForm(group.key, {
                                            delivery_point_number: '',
                                            nova_poshta_warehouse_ref: '',
                                            nova_poshta_warehouse_number: '',
                                            nova_poshta_warehouse_address: '',
                                        });
                                    }}
                                    onFallbackInput={(val) =>
                                        onUpdateField(
                                            group.key,
                                            'delivery_point_number',
                                            val,
                                        )
                                    }
                                    error={getError('delivery_point_number')}
                                    isFallback={!form.nova_poshta_city_ref}
                                    allowManualEntry={false}
                                />
                            </div>
                        )}

                        {form.method === 'ukrposhta' && !showSavedSummary && (
                            <div className="space-y-4">
                                <UkrposhtaSettlementSelect
                                    value={
                                        form.ukrposhta_settlement_name ||
                                        form.city
                                    }
                                    onSelect={(s) => {
                                        onPatchForm(group.key, {
                                            city: s.name,
                                            region: s.region,
                                            ukrposhta_settlement_ref: s.ref,
                                            ukrposhta_settlement_name: s.name,
                                            post_office: '',
                                            ukrposhta_post_office_ref: '',
                                            ukrposhta_post_office_index: '',
                                            ukrposhta_post_office_address: '',
                                            ukrposhta_post_office_name: '',
                                        });
                                    }}
                                    onFallbackInput={(val) => {
                                        onUpdateField(group.key, 'city', val);
                                        onUpdateField(
                                            group.key,
                                            'ukrposhta_settlement_name',
                                            val,
                                        );
                                    }}
                                    error={getError('city')}
                                />

                                <UkrposhtaOfficeSelect
                                    settlementRef={
                                        form.ukrposhta_settlement_ref
                                    }
                                    value={
                                        form.ukrposhta_post_office_address
                                            ? `${form.ukrposhta_post_office_index ? form.ukrposhta_post_office_index + ' — ' : ''}${form.ukrposhta_post_office_address}`
                                            : form.post_office
                                    }
                                    onSelect={(office) => {
                                        onPatchForm(group.key, {
                                            post_office: office.address,
                                            ukrposhta_post_office_ref:
                                                office.ref,
                                            ukrposhta_post_office_index:
                                                office.index,
                                            ukrposhta_post_office_address:
                                                office.address,
                                            ukrposhta_post_office_name:
                                                office.name,
                                        });
                                    }}
                                    onFallbackInput={(val) =>
                                        onUpdateField(
                                            group.key,
                                            'post_office',
                                            val,
                                        )
                                    }
                                    error={getError('post_office')}
                                    isFallback={!form.ukrposhta_settlement_ref}
                                />
                            </div>
                        )}

                        {form.method === 'pickup' && (
                            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/50 dark:bg-blue-500/10 dark:text-blue-300">
                                Продавець звʼяжеться з вами для узгодження місця
                                та часу передачі.
                            </div>
                        )}

                        <div>
                            <label className="mb-1 block text-sm font-medium">
                                Коментар для продавця
                            </label>
                            <Input
                                value={form.comment_for_seller}
                                onChange={(e) =>
                                    onUpdateField(
                                        group.key,
                                        'comment_for_seller',
                                        e.target.value,
                                    )
                                }
                                placeholder="Додаткова інформація..."
                            />
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                        У цього продавця не налаштовано жодного способу
                        доставки.
                    </p>
                )}
            </CardContent>
        </Card>
    );
};
